import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useStateSelector } from "utils/field";
import { isEmpty } from "lodash";

import { selectFormErrors } from "store/form/selectors";
import { selectPackageResults } from "features/Model/Review/Results/_ducks/selectors";

import classes from "./style.module.scss";

export const ChecklistPopover = ({ items, label, title, className, rowLimit = 5 }) => {
    const pendingItems = items.filter(({ completed }) => !completed);

    if (pendingItems.length === 0) {
        return null;
    }

    return (
        <div className={`${classes.checklistPopover} ${className || ""}`}>
            {title && <div className={classes.checklistTitle}>{title}</div>}
            <div className={classes.checklistList} style={{ gridTemplateRows: `repeat(${rowLimit}, auto)` }}>
                {pendingItems.map(({ label: itemLabel }, i) => (
                    <div key={i} className={classes.checklistItem}>
                        <span className={classes.checkStatus}>
                            <span className={classes.checkPending}>○</span>
                        </span>
                        <span className={classes.checklistItemPending}>{itemLabel}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// App palette (matches assets/styles/_variables.scss) for use in JS
export const APP_COLORS = {
    red: "#c21818",
    orange: "#f58020",
    gold: "#ffb22d",
    green: "#18c1ad",
    grey: "#92a4ad",
};

export const getColorByPercentage = (percentage) => {
    const p = Math.min(100, Math.max(0, percentage));
    if (p === 0) return APP_COLORS.red;
    if (p < 80) return APP_COLORS.orange;
    if (p < 100) return APP_COLORS.gold;
    return APP_COLORS.green;
};

export function useTabCompletions() {
    const weather = useStateSelector("model.modelData.location.weather") || {};
    const agFloorArea = useStateSelector("model.modelData.dimensions.aboveGradeIntFloorArea.total") ?? 0;
    const bgFloorArea = useStateSelector("model.modelData.dimensions.belowGradeIntFloorArea.total") ?? 0;
    const hasFloorArea = (agFloorArea || 0) + (bgFloorArea || 0) > 0;
    const volumeTotal = useStateSelector("model.modelData.dimensions.volume.total") ?? 0;
    const hasVolume = Number(volumeTotal) > 0;

    const f280Warning = weather.f280?.location_warning || "";
    const needsWeatherReview = Boolean(useStateSelector("model.modelData.uiSettings.completion.needsWeatherReview"));
    const needsYearBuiltReview = Boolean(
        useStateSelector("model.modelData.uiSettings.completion.needsYearBuiltReview")
    );
    const needsHeatingReview = Boolean(useStateSelector("model.modelData.uiSettings.completion.needsHeatingReview"));

    const components = useStateSelector("model.modelData.components") || {};
    const basement = components.basement || {};
    const expFloor = components.expFloor || {};
    const wall = components.wall || {};
    const ceiling = components.ceiling || {};
    const crawlspace = components.crawlspace || {};
    const slab = components.slab || {};
    const hasFloors = [basement, crawlspace, slab, expFloor].some(
        (compObj) => Object.keys(compObj).length > 0 && !Object.values(compObj).every(isEmpty)
    );

    const hasWindows = [wall, ceiling, basement, crawlspace].some((compObj) => {
        if (!compObj || Object.keys(compObj).length === 0) return false;
        return Object.values(compObj).some((comp) => {
            const windows = comp?.subcomponents?.window || {};
            const doors = comp?.subcomponents?.door || {};
            const doorWindows = Object.values(doors || {}).some(
                (d) => (d?.subcomponents?.window && Object.keys(d.subcomponents.window).length > 0) || false
            );
            return (Object.keys(windows).length > 0 && !Object.values(windows).every(isEmpty)) || doorWindows;
        });
    });
    const hasWall = Object.keys(wall).length > 0 && !Object.values(wall).every(isEmpty);
    const hasCeiling = Object.keys(ceiling).length > 0 && !Object.values(ceiling).every(isEmpty);

    const hasBasement = Object.keys(basement).length > 0 && !Object.values(basement).every(isEmpty);
    const dhwPrimarySystem = useStateSelector("model.modelData.domesticHotWater.primarySystem") || {};
    const dhwTankLocation = useStateSelector("model.modelData.domesticHotWater.primarySystem.tankLocation") || {};
    const dhwTankLocationId = dhwTankLocation?.id;
    const dhwTankInBasement = dhwTankLocationId === 1 || dhwTankLocationId === "1";
    const hasDhw =
        Object.keys(dhwPrimarySystem).length > 0 &&
        !isEmpty(dhwPrimarySystem) &&
        dhwTankLocationId != null &&
        dhwTankLocationId !== "" &&
        (!hasBasement || dhwTankInBasement);

    const heatingSystem = useStateSelector("model.modelData.heatingCooling.heating.system") || {};
    const hasHeating = Object.keys(heatingSystem).length > 0;

    const airTestType = useStateSelector("model.modelData.airTightness.testType") || {};
    const airTestTypeId = airTestType.id;
    const ach50 = useStateSelector("model.modelData.airTightness.blowerTest.airChangeRate50Pa");
    const eqLeakage = useStateSelector("model.modelData.airTightness.blowerTest.eqLeakageArea");
    const hasTestType = airTestTypeId != null && airTestTypeId !== "";
    const hasBlowerData =
        (ach50 != null && ach50 !== "" && Number(ach50) > 0) ||
        (eqLeakage != null && eqLeakage !== "" && Number(eqLeakage) > 0);

    const hasEla = Number(eqLeakage) > 0 && hasVolume;

    return useMemo(() => {
        const generalChecks = [
            { label: "Weather reviewed", completed: !needsWeatherReview },
            { label: "Year built reviewed", completed: !needsYearBuiltReview },
            { label: "F280 weather verified", completed: !f280Warning },
        ];
        const enclosureChecks = [
            { label: "Floors", completed: hasFloors },
            { label: "Wall", completed: hasWall },
            { label: "Windows", completed: hasWindows },
            { label: "Ceiling", completed: hasCeiling },
            { label: "Floor area", completed: hasFloorArea },
            { label: "House volume", completed: hasVolume },
        ];
        const systemsChecks = [
            {
                label: needsHeatingReview ? "Heating system reviewed" : "Primary heating system",
                completed: needsHeatingReview ? false : hasHeating,
            },
            {
                label: hasBasement ? "DHW (tank must be in basement)" : "DHW",
                completed: hasDhw,
            },
        ];
        const airtightnessChecks = [
            { label: "Test type selected", completed: hasTestType },
            { label: "Blower test data", completed: hasBlowerData },
            { label: "Valid ELA", completed: hasEla },
        ];

        const percentComplete = (checks) =>
            checks.length === 0 ? 0 : Math.round((checks.filter((c) => c.completed).length / checks.length) * 100);

        return {
            completions: {
                general: percentComplete(generalChecks),
                enclosure: percentComplete(enclosureChecks),
                systems: percentComplete(systemsChecks),
                airtightness: percentComplete(airtightnessChecks),
            },
            checklists: {
                general: generalChecks,
                enclosure: enclosureChecks,
                systems: systemsChecks,
                airtightness: airtightnessChecks,
            },
        };
    }, [
        f280Warning,
        needsWeatherReview,
        needsYearBuiltReview,
        needsHeatingReview,
        hasFloorArea,
        hasVolume,
        hasFloors,
        hasWall,
        hasWindows,
        hasCeiling,
        hasBasement,
        hasDhw,
        hasHeating,
        hasTestType,
        hasBlowerData,
        hasEla,
    ]);
}

const DISABLED_H2K_VERSIONS = ["v11-9", "v11-10"];

export function useSimulationChecks() {
    const formErrors = useSelector(selectFormErrors) || [];
    const model = useStateSelector("model") || {};
    const { modelData = {} } = model;
    const { h2kVersion: h2kVersionObj = {}, program = {} } = modelData;
    const { major: h2kMajor, minor: h2kMinor } = h2kVersionObj;
    const h2kVersion = `v${h2kMajor}-${h2kMinor}`;
    const programClass = program.class || "";
    const user = useStateSelector("user") || {};
    const nonErsUser = user.nonErsUser === true;

    const checks = useMemo(() => {
        const hot2000Ok = !DISABLED_H2K_VERSIONS.includes(h2kVersion);
        const programModeOk = !nonErsUser || programClass === "";
        const noFormErrors = formErrors.length === 0;
        return [
            {
                label: "H2K v11.11 or newer",
                completed: hot2000Ok,
            },
            {
                label: "Program mode (General if non-ERS)",
                completed: programModeOk,
            },
            {
                label: "No form errors",
                completed: noFormErrors,
            },
        ];
    }, [h2kVersion, nonErsUser, programClass, formErrors.length]);

    const completionPercentage = useMemo(() => {
        const completed = checks.filter((c) => c.completed).length;
        return Math.round((completed / checks.length) * 100);
    }, [checks]);

    return { checks, completionPercentage };
}

const SIMULATION_HIDDEN_SECTIONS = ["general", "enclosure", "systems", "airtightness"];

// Enclosure represents the bulk of the modelling effort, and the simulation gate checks pass
// on an untouched model, so items are weighted to keep empty models near zero.
const SECTION_WEIGHTS = {
    simulation: 1,
    general: 1,
    enclosure: 3,
    systems: 2,
    airtightness: 1,
};

export function useSimulationCompletion() {
    const { checklists } = useTabCompletions();
    const { checks: simulationChecks } = useSimulationChecks();
    const cliId = useStateSelector("model.modelDetails.lastCliRun.cliId") || {};
    const packageResults = useSelector(selectPackageResults) || {};

    const simulationCompletion = useMemo(() => {
        const weightedSections = [
            { checks: simulationChecks, weight: SECTION_WEIGHTS.simulation },
            ...SIMULATION_HIDDEN_SECTIONS.map((section) => ({
                checks: checklists[section] || [],
                weight: SECTION_WEIGHTS[section],
            })),
        ];

        const { earned, total } = weightedSections.reduce(
            (acc, { checks, weight }) => ({
                earned: acc.earned + checks.filter((c) => c.completed).length * weight,
                total: acc.total + checks.length * weight,
            }),
            { earned: 0, total: 0 }
        );

        return total === 0 ? 0 : Math.round((earned / total) * 100);
    }, [simulationChecks, checklists]);

    const attentionChecks = useMemo(() => {
        const seenLabels = new Set();
        const tabChecks = SIMULATION_HIDDEN_SECTIONS.flatMap((section) => checklists[section] || []);

        return [...simulationChecks, ...tabChecks].filter(({ label, completed }) => {
            if (completed || seenLabels.has(label)) return false;
            seenLabels.add(label);
            return true;
        });
    }, [simulationChecks, checklists]);

    // Used for the complete-state tooltip copy ("Model complete" vs "Model ready for simulation").
    // Does not lock the header icon — completion still follows the live checklist.
    const hasSimulationResults = Object.keys(cliId).length > 0 || Object.keys(packageResults).length > 0;

    const modelComplete = useMemo(
        () => simulationChecks.every(({ completed }) => completed) && attentionChecks.length === 0,
        [simulationChecks, attentionChecks]
    );

    return { simulationCompletion, simulationChecks, attentionChecks, modelComplete, hasSimulationResults };
}

const ModelProgressBar = () => {
    const { simulationCompletion } = useSimulationCompletion();

    return (
        <div className={classes.modelProgressBar}>
            <div className={classes.barTrack}>
                <div className={classes.barFill} style={{ width: `${simulationCompletion}%` }} />
            </div>
            <span className={classes.barLabel}>{simulationCompletion}%</span>
        </div>
    );
};

export default ModelProgressBar;
